import React, { useCallback, useEffect, useMemo, useState } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import toast from "react-hot-toast";
import * as Yup from "yup";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../../inputs/TextInput";
import { useTranslation } from "react-i18next";
import { APP_ROUTES } from "../../../routes";
import { useNavigate } from "react-router-dom";
import { ApproveAppointmentBody } from "../../../hooks/visitor/interface";
import {
  useAddAttachment,
  useApproveAppointment,
  useApproveDevice,
} from "../../../hooks/visitor/mutation";
import useAuthStore from "../../../store/Auth";
import {
  AddAttachments,
  API_ERRORS,
  Device,
  DEVICES_APPROVE_REQUEST_DETAILS_TABLE,
  STATUS_IDS,
  USER_ROLES_IDS,
} from "../../../constants/common";
import DeviceApprovalDetailsTable from "../../table/DeviceApprovalDetailsTable";
import { cn } from "../../../utils/cn";
import VehicleAllowedThreeStateToggle from "../../toggle/ThreeStateToggle/VehicleAllowedThreeStateToggle";

interface ApproveAppointmentModalProps {
  data?: ApproveAppointmentBody;
  isOpen: boolean;
  onOpenChange: () => void;

  addedDevices: any;

  isVehicleAllowedByPreviousRole?: boolean;

  appointmentDateTime: {
    visitStartDate?: string;
    visitEndDate?: string;
  };

  isDevicesApproval: boolean;
  appointmentId?: number;
  isUserAppointmentApprover?: boolean;
  approvalStatus?: STATUS_IDS;
}

const ApproveAppointmentModalSchema = Yup.object({
  reason: Yup.string(),
});

interface ApproveAppointmentModalData {
  reason?: string;
}

const ApproveAppointmentModal = ({
  appointmentId,
  data,
  isOpen,
  onOpenChange,
  addedDevices,
  isDevicesApproval,
  appointmentDateTime,
  isUserAppointmentApprover,
  approvalStatus,
  isVehicleAllowedByPreviousRole,
}: ApproveAppointmentModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useFormWithSchema(ApproveAppointmentModalSchema);

  const { t } = useTranslation();

  const user = useAuthStore((state) => state.user);

  const [isVehicleAllowed, setIsVehicleAllowed] = useState(
    isVehicleAllowedByPreviousRole
  );

  useEffect(() => {
    setIsVehicleAllowed(isVehicleAllowedByPreviousRole);
  }, [isVehicleAllowedByPreviousRole]);

  const {
    mutateAsync: UpdateVisitorAppointmentApprovalStatus,
    isPending: isUpdateVisitorAppointmentApprovalStatusLoading,
  } = useApproveAppointment();

  const { mutateAsync: ApproveDevices, isPending: isApproveDevicesLoading } =
    useApproveDevice();

  const navigate = useNavigate();

  const handleDevicesApproval = useCallback(async () => {
    if (appointmentId) {
      try {
        const body = {
          EncryptedId: appointmentId.toString(),
          n_RoleID: user?.n_RoleID,
          approvalDevices: addedDevices?.map((item: Device) => ({
            appointmentDeviceId: item.appointmentDeviceId,
            N_ApprovalStatus: item.approvalStatus || 0,
            N_CurrentApprovalStatus: item.currentUserDeviceStatus || 0,
            // s_Comment: item.comment,
          })),
          Dt_AppointmentStartTime: appointmentDateTime?.visitStartDate,
          Dt_AppointmentEndTime: appointmentDateTime?.visitEndDate,
        };

        await ApproveDevices(body);
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        return;
      } finally {
        onOpenChange();
      }
    }
  }, [
    appointmentId,
    user?.n_RoleID,
    addedDevices,
    appointmentDateTime?.visitStartDate,
    appointmentDateTime?.visitEndDate,
    ApproveDevices,
    t,
    onOpenChange,
  ]);

  const isStaffResubmitting = useMemo(
    () =>
      user?.n_RoleID === USER_ROLES_IDS.STAFF &&
      isUserAppointmentApprover &&
      approvalStatus === STATUS_IDS.REQUEST_INFO,
    [approvalStatus, isUserAppointmentApprover, user?.n_RoleID]
  );

  const { mutateAsync: AddAttachment, isPending: isAddAttachmentLoading } =
    useAddAttachment();

  const handleApproveAppointment = useCallback(
    async (formData: ApproveAppointmentModalData) => {
      if (
        data?.s_VehicleNo &&
        (user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
          user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER) &&
        isVehicleAllowed === undefined
      ) {
        toast.error(t("kindlySelectApproveVehicleEntryPermit"));
        return;
      }

      if (isStaffResubmitting && !formData.reason) {
        toast.error(t("addResubmissionComment"));
        return;
      }

      if (isDevicesApproval) {
        handleDevicesApproval();
      }

      try {
        if (data) {
          const body = {
            ...data,
            N_IsVehicleAllowed: isVehicleAllowed ? 1 : 0,
            N_IsVehicleAllowedUpdate:
              user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
              user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER
                ? 1
                : 0,
            ...{
              [!isStaffResubmitting
                ? "s_HostComments"
                : "S_HostResubmissionComment"]: formData.reason || null,
            },
          };
          await UpdateVisitorAppointmentApprovalStatus(body);

          if (user?.n_RoleID === USER_ROLES_IDS.STAFF && appointmentId) {
            const attachmentBody: AddAttachments = {
              Id: appointmentId,
              File: data.uploadHostEmployeeCard,
              S_FileType: data.hostEmployeeCardFileType,
              S_FileFieldName: "S_HostEmployeeCardFile",
              S_FiledFileType: "S_HostEmployeeCardContentType",
            };

            const formData = new FormData();
            formData.append("Id", attachmentBody.Id.toString());
            formData.append("File", attachmentBody.File ?? "");
            formData.append("S_FileType", attachmentBody.S_FileType ?? "");
            formData.append("S_FileFieldName", attachmentBody.S_FileFieldName);
            formData.append("S_FiledFileType", attachmentBody.S_FiledFileType);

            try {
              if (attachmentBody.File) {
                await AddAttachment(formData);
              }

              // await Promise.all(uploadPromises);
            } catch (error) {
              toast.error(t("somethingWentWrongAttachments"));
            }
          }

          toast.success(t("appointmentApproved"));

          setTimeout(() => {
            navigate(APP_ROUTES.APPROVE_VISIT_REQUEST);
          }, 700);
        }
      } catch (error: any) {
        const errorCode = error.errors && Object.keys(error.errors)[0];

        if (errorCode === API_ERRORS.MAX_ALLOWED_DAYS_EXCEEDED) {
          toast.error(t("maxAllowedDaysExceeded"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
      } finally {
        onOpenChange();
      }
    },
    [
      AddAttachment,
      UpdateVisitorAppointmentApprovalStatus,
      appointmentId,
      data,
      handleDevicesApproval,
      isDevicesApproval,
      isStaffResubmitting,
      isVehicleAllowed,
      navigate,
      onOpenChange,
      t,
      user?.n_RoleID,
    ]
  );

  const onClose = useCallback(() => {
    reset({ reason: "" });
    onOpenChange();
  }, [onOpenChange, reset]);

  return (
    <ModalWrapper
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      contentStyles="lg:max-w-[90vw]"
    >
      <div className="max-h-[70vh] overflow-y-auto w-full pb-1">
        <form
          onSubmit={handleSubmit(handleApproveAppointment)}
          className="flex flex-col gap-5 justify-center items-center w-full"
        >
          <p className="text-lg font-medium">{t("confirmApproval")}</p>

          <div className="w-[80vw] lg:w-[70vw]">
            {addedDevices?.length > 0 && isDevicesApproval && (
              <DeviceApprovalDetailsTable
                devices={addedDevices}
                type={
                  DEVICES_APPROVE_REQUEST_DETAILS_TABLE.APPROVE_DETAILS_TABLE
                }
              />
            )}
          </div>

          <div
            className={cn(
              "flex flex-col gap-3 w-3/4",
              !isDevicesApproval && "-mt-4"
            )}
          >
            {data?.s_VehicleNo &&
              (user?.n_RoleID === USER_ROLES_IDS.SECURITY ||
                user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER) && (
                <VehicleAllowedThreeStateToggle
                  isVehicleAllowed={isVehicleAllowed}
                  setIsVehicleAllowed={setIsVehicleAllowed}
                />
              )}

            <Controller
              name="reason"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={
                    isStaffResubmitting
                      ? t("resubmissionComment")
                      : t("commentsOptional")
                  }
                  placeholder={
                    !isStaffResubmitting ? t("leaveCommentOptional") : ""
                  }
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  value={value}
                  required={isStaffResubmitting}
                />
              )}
            />
          </div>

          <div className="flex gap-2 items-center">
            <Button
              isLoading={
                isApproveDevicesLoading ||
                isUpdateVisitorAppointmentApprovalStatusLoading ||
                isAddAttachmentLoading
              }
            >
              {t("approve")}
            </Button>

            <Button
              onClick={onClose}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("close")}
            </Button>
          </div>
        </form>
      </div>
    </ModalWrapper>
  );
};

export default React.memo(ApproveAppointmentModal);
